import React, {useState, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import {format} from 'date-fns';

// @mui
import {styled, alpha, useTheme} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';

// components
import Iconify from 'packages/common/components/iconify';

// API
import {useReservationData} from 'packages/common/components/table-reservation/context/ReservationDataContext';

// utils
import {useLocalizedText} from 'packages/common/utils/multiLanguageHelper';

/**
 * @file Étape de choix de la salle du flow de réservation.
 * @module packages.common.components.table-reservation.sections.RoomStep
 *
 * Présente les salles réservables en ligne sous forme de cartes sélectionnables,
 * enrichies de leur disponibilité pour la date/le service/la taille de groupe
 * choisis. Afficher l'état de chaque salle (« Disponible » / « Complet ») évite
 * l'abandon « tout est complet » : le client voit immédiatement qu'une autre salle
 * a encore de la place. Une salle complète n'est pas sélectionnable. Le créneau est
 * recalculé pour la salle choisie à l'étape suivante (`TimeStep`, paramètre `areaId`).
 */

// ----------------------------------------------------------------------

const RoomCard = styled(ButtonBase, {
  shouldForwardProp: (prop) => !['selected', 'primaryColor', 'unavailable'].includes(prop),
})(({theme, selected, primaryColor, unavailable}) => {
  const accent = primaryColor || theme.palette.primary.main;
  return {
    'width': '100%',
    'height': '100%',
    'textAlign': 'left',
    'alignItems': 'center',
    'justifyContent': 'flex-start',
    'gap': theme.spacing(1.5),
    'padding': theme.spacing(1.75, 2),
    'borderRadius': 14,
    'border': `1.5px solid ${selected ? accent : theme.palette.divider}`,
    'backgroundColor': selected ? alpha(accent, 0.06) : theme.palette.background.paper,
    'boxShadow': selected ? `0 4px 14px ${alpha(accent, 0.16)}` : 'none',
    'transition': theme.transitions.create(['border-color', 'background-color', 'box-shadow', 'transform'], {
      duration: 160,
    }),
    'opacity': unavailable ? 0.85 : 1,
    'cursor': unavailable ? 'not-allowed' : 'pointer',
    '&:hover': {
      borderColor: accent,
      backgroundColor: selected ? alpha(accent, 0.08) : alpha(accent, 0.03),
      transform: 'translateY(-1px)',
    },
    '&.Mui-disabled': {
      opacity: 0.85,
      transform: 'none',
    },
    '&:focus-visible': {
      outline: `2px solid ${accent}`,
      outlineOffset: 2,
    },
  };
});

const StatusPill = styled('span', {
  shouldForwardProp: (prop) => prop !== 'available',
})(({theme, available}) => {
  const palette = available ? theme.palette.success : theme.palette.error;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0.875),
    borderRadius: 999,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 600,
    lineHeight: 1.4,
    color: palette.dark,
    backgroundColor: alpha(palette.main, 0.12),
  };
});

// ----------------------------------------------------------------------

/**
 * Étape de sélection de la salle.
 *
 * @param {Object} props
 * @param {Object} props.formData - Données du formulaire (date, service, partySize, selectedAreaId, availableRooms)
 * @param {Function} props.updateFormData - Met à jour un champ du formulaire
 * @param {Function} props.setValidation - Enregistre la fonction de validation auprès du parent
 * @param {Function} props.setErrors - Définit les erreurs de validation
 * @param {Object} [props.errors] - Erreurs de validation courantes
 * @param {Function} [props.setStepReady] - Active/désactive le bouton suivant selon la sélection
 * @param {string} [props.primaryColor] - Couleur primaire du thème
 * @param {Function} [props.t] - Fonction de traduction générique
 * @return {JSX.Element}
 */
export function RoomStep({
  formData,
  updateFormData,
  config,
  setValidation,
  setErrors,
  errors,
  setStepReady,
  primaryColor,
  t,
  onNextStep,
}) {
  const theme = useTheme();
  const {getBookableRooms} = useReservationData();

  // Message personnalisé du restaurateur pour cette étape (multilingue), résolu
  // dans la langue courante du widget. Vide = aucun encart affiché.
  const getLocalizedText = useLocalizedText();
  const roomMessage = getLocalizedText(config?.roomSelectionMessage);

  // Choix obligatoire (défaut) ou facultatif. En facultatif, le client peut poursuivre
  // sans salle ciblée via le bouton « Continuer sans préférence » (cf. ReservationFlow).
  const roomSelectionRequired = config?.roomSelectionRequired !== false;

  // Amorçage depuis la liste déjà chargée (useRoomSelection), remplacée ensuite par
  // la disponibilité propre à la date choisie. Pendant ce calcul, on affiche un
  // squelette plutôt que des cartes sans information de disponibilité.
  const [rooms, setRooms] = useState(() => formData.availableRooms || []);
  const [isLoading, setIsLoading] = useState(true);

  const tr = useCallback(
      (key, fallback) => {
        if (typeof t !== 'function') return fallback;
        const value = t(key);
        return value === key ? fallback : value;
      },
      [t],
  );

  const validateStep = useCallback(() => {
    // Bloquant uniquement si le choix est obligatoire et qu'aucune salle n'est choisie.
    if (roomSelectionRequired && !formData.selectedAreaId) {
      setErrors({room: tr('widgets.reservation.form.roomRequired', 'Veuillez choisir une salle.')});
      return false;
    }
    return true;
  }, [roomSelectionRequired, formData.selectedAreaId, setErrors, tr]);

  useEffect(() => {
    setValidation(validateStep);
  }, [setValidation, validateStep]);

  useEffect(() => {
    if (setStepReady) {
      // Facultatif : « Suivant » reste actif sans sélection (poursuite sans préférence).
      setStepReady(!roomSelectionRequired || !!formData.selectedAreaId);
    }
  }, [roomSelectionRequired, formData.selectedAreaId, setStepReady]);

  // Recharge la disponibilité par salle dès que la date/le service/la taille change.
  useEffect(() => {
    if (typeof getBookableRooms !== 'function' || !formData.date || !formData.service || !formData.partySize) {
      setIsLoading(false);
      return undefined;
    }

    const dateString = format(formData.date, 'yyyy-MM-dd');

    // Pas de garde par ref persistante (même piège StrictMode que useRoomSelection) :
    // une ref survivrait au double-montage dev et le 1er fetch annulé laisserait les
    // salles sans info de disponibilité. Les dépendances étant stables, l'effet ne
    // tourne qu'une fois en production.
    let cancelled = false;
    setIsLoading(true);

    (async () => {
      try {
        const result = await getBookableRooms({
          date: dateString,
          service: formData.service,
          partySize: formData.partySize,
        });
        if (cancelled) return;
        setRooms(Array.isArray(result) ? result : []);
      } catch {
        if (cancelled) return;
        // Dégradation gracieuse : on garde la liste amorcée (sans badge de dispo).
        setRooms(formData.availableRooms || []);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getBookableRooms, formData.date, formData.service, formData.partySize, formData.availableRooms]);

  // Si la salle déjà choisie devient complète, ou disparaît de la liste parce
  // qu'elle est fermée pour ce service à la nouvelle date, on annule la sélection :
  // impossible de poursuivre sur une salle sans créneau. On attend la fin du
  // chargement pour ne pas effacer le choix pendant le rechargement de la liste.
  useEffect(() => {
    if (isLoading || !formData.selectedAreaId) return;
    const selectedRoom = rooms.find((room) => room.id === formData.selectedAreaId);
    if (!selectedRoom || selectedRoom.available === false) {
      updateFormData('selectedAreaId', null);
    }
  }, [isLoading, rooms, formData.selectedAreaId, updateFormData]);

  const handleSelect = (room) => {
    // Une salle complète n'est pas sélectionnable : on évite d'envoyer le client
    // dans un cul-de-sac (étape créneaux vide).
    if (room.available === false) return;

    // Comportement « à cocher / décocher » : recliquer la salle déjà choisie la
    // désélectionne. Aucune salle n'est sélectionnée par défaut.
    if (room.id === formData.selectedAreaId) {
      updateFormData('selectedAreaId', null);
      return;
    }

    updateFormData('selectedAreaId', room.id);

    // La salle conditionne les créneaux : on invalide le créneau précédemment choisi
    // pour forcer une nouvelle sélection adaptée à la salle.
    if (formData.timeSlot) {
      updateFormData('timeSlot', null);
      updateFormData('selectedTimeSlotData', null);
    }

    if (errors.room) {
      setErrors({});
    }
  };

  const hasAvailabilityInfo = rooms.some((room) => typeof room.available === 'boolean');
  const allUnavailable = hasAvailabilityInfo && rooms.every((room) => room.available === false);
  // Le backend OMET les salles fermées pour ce service (contrairement aux salles
  // complètes, renvoyées avec `available: false`) : la liste peut donc être vide.
  // Sans ce cas, l'étape afficherait une grille vide sans explication, « Suivant »
  // resterait bloqué, et le client n'aurait aucune piste pour avancer.
  const hasNoRoom = !isLoading && rooms.length === 0;

  // Nombre de cartes squelette pendant le calcul : calé sur le nombre de salles
  // déjà connu (useRoomSelection) pour un rendu stable, minimum 2.
  const skeletonCount = Math.max(formData.availableRooms?.length || 0, 2);

  return (
    <Fade in timeout={300}>
      <Box sx={{pb: 3}}>
        {roomSelectionRequired ? (
          <Typography variant="h6" sx={{fontWeight: 600, mb: 3, textAlign: 'center'}}>
            {tr('widgets.reservation.form.roomQuestion', 'Choisissez votre salle')}
          </Typography>
        ) : (
          // En mode facultatif : titre à gauche + lien « Passer cette étape » à droite
          // (même pattern que l'étape Formules). Le lien efface la salle choisie.
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 3}}>
            <Typography variant="h6" sx={{fontWeight: 600}}>
              {tr('widgets.reservation.form.roomQuestion', 'Choisissez votre salle')}
            </Typography>
            <Typography
              component="button"
              variant="body2"
              onClick={() => {
                // Poursuite sans préférence : on efface la salle puis on avance directement.
                updateFormData('selectedAreaId', null);
                if (formData.timeSlot) {
                  updateFormData('timeSlot', null);
                  updateFormData('selectedTimeSlotData', null);
                }
                if (errors.room) {
                  setErrors({});
                }
                if (typeof onNextStep === 'function') {
                  onNextStep();
                }
              }}
              sx={{
                'background': 'none',
                'border': 'none',
                'cursor': 'pointer',
                'color': 'text.secondary',
                'textDecoration': 'underline',
                'textUnderlineOffset': 3,
                'p': 0,
                'fontFamily': 'inherit',
                'typography': 'body2',
                'whiteSpace': 'nowrap',
                'flexShrink': 0,
                '&:hover': {color: 'text.primary'},
              }}
            >
              {tr('widgets.reservation.form.roomSkipStep', 'Passer cette étape')}
            </Typography>
          </Box>
        )}

        {roomMessage && roomMessage.trim() && (
          <Fade in timeout={400}>
            {/* Même style sobre que la note de service (cf. ServiceNote) plutôt que
                le turquoise par défaut de l'Alert info, avec une petite icône. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                mb: 2.5,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                backgroundColor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Iconify
                icon="mdi:information-outline"
                width={18}
                sx={{color: 'text.secondary', flexShrink: 0, mt: '1px'}}
              />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.5,
                  fontWeight: 400,
                  wordBreak: 'break-word',
                }}
              >
                {roomMessage}
              </Typography>
            </Box>
          </Fade>
        )}

        {allUnavailable && (
          <Fade in timeout={400}>
            <Alert severity="warning" sx={{mb: 2.5, borderRadius: 2, py: 0.5}}>
              {tr(
                  'widgets.reservation.form.roomAllFull',
                  'Toutes les salles sont complètes à cette date. Essayez une autre date.',
              )}
            </Alert>
          </Fade>
        )}

        {hasNoRoom && (
          <Fade in timeout={400}>
            <Alert severity="warning" sx={{mb: 2.5, borderRadius: 2, py: 0.5}}>
              {tr(
                  'widgets.reservation.form.roomNone',
                  'Aucune salle n\'est proposée à cette date. Essayez une autre date.',
              )}
            </Alert>
          </Fade>
        )}

        {isLoading ? (
          <Grid container spacing={2} justifyContent="center">
            {[...Array(skeletonCount)].map((_, index) => (
              <Grid item xs={12} sm={6} key={`room-skeleton-${index}`}>
                <Skeleton variant="rounded" width="100%" height={74} sx={{borderRadius: '14px'}} animation="wave" />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={2} justifyContent="center">
            {rooms.map((room) => {
              const selected = room.id === formData.selectedAreaId;
              const hasInfo = typeof room.available === 'boolean';
              const available = room.available !== false;
              const unavailable = hasInfo && !available;

              return (
                <Grid item xs={12} sm={6} key={room.id}>
                  <RoomCard
                    selected={selected}
                    primaryColor={primaryColor}
                    unavailable={unavailable}
                    disabled={unavailable}
                    onClick={() => handleSelect(room)}
                    aria-pressed={selected}
                  >
                    <Box sx={{minWidth: 0, flexGrow: 1}}>
                      <Typography
                        variant="subtitle1"
                        noWrap
                        sx={{fontWeight: 600, lineHeight: 1.3, color: unavailable ? 'text.secondary' : 'text.primary'}}
                      >
                        {room.name}
                      </Typography>
                      {hasInfo && (
                        <StatusPill available={available}>
                          <Iconify icon={available ? 'mdi:check-circle' : 'mdi:close-circle'} width={11} />
                          {available ?
                            tr('widgets.reservation.form.roomAvailable', 'Disponible') :
                            tr('widgets.reservation.form.roomFull', 'Complet')}
                        </StatusPill>
                      )}
                    </Box>

                    {selected && (
                      <Iconify
                        icon="mdi:check-circle"
                        width={22}
                        sx={{color: primaryColor || theme.palette.primary.main, flexShrink: 0}}
                      />
                    )}
                  </RoomCard>
                </Grid>
              );
            })}
          </Grid>
        )}

        {errors.room && (
          <Alert severity="error" sx={{mt: 2, borderRadius: 2, py: 0.5}}>
            {errors.room}
          </Alert>
        )}
      </Box>
    </Fade>
  );
}

RoomStep.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  config: PropTypes.object,
  setValidation: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setStepReady: PropTypes.func,
  primaryColor: PropTypes.string,
  t: PropTypes.func,
  onNextStep: PropTypes.func,
};
